import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Box, Button, Typography } from '@mui/material';
import { useCalories } from 'src/hooks/useCalories';
import { useResponsive } from 'src/hooks/useResponsive';
import {
    getAllModifierProductsForRootReferenceId,
    IBasketItem,
    IItemReadResourceV10
} from 'components/basket/model/Basket';
import { getProductOptionsDetails } from 'components/menu/model/Menu';
import { IOrderReadResourceV18 } from 'components/order/model/Order';
import { RecentOrderItemBox } from 'components/recentOrders/RecentOrderItemBox';
import { CONTENT_MAX_WIDTH } from 'config/constants';
import { BottomDialog } from 'lib/BottomDialog';
import { getCurrencyString, getFadeDownGradient, showCalories } from 'lib/helpers';
import { addBasketItems } from 'store/basket/basketActions';
import { useParams } from 'react-router';
import { LocationRouteParams } from 'pages/routes';
import { EGAEventName, isSweetwaters, useGAHelpers } from 'lib/useGAHelpers';
import { uuid } from 'uuidv4';
import { constructBasketItemsTree } from 'components/basket/helpers';

const PREFIX = 'RecentOrderItemsSelection';

const classes = {
    buttonWrapper: `${PREFIX}buttonWrapper`,
    buttonContainer: `${PREFIX}buttonContainer`,
    addButton: `${PREFIX}addButton`
};

const StyledButtonContainer = styled(Box, {
    shouldForwardProp: prop => prop !== 'isDesktop'
})<{ isDesktop: boolean }>(({ theme, isDesktop }) => ({
    paddingTop: theme.spacing(2),
    ...(isDesktop
        ? {
              minWidth: '400px'
          }
        : {}),

    [`& .${classes.buttonWrapper}`]: {
        background: getFadeDownGradient(theme.palette.background.default),
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        maxWidth: `${CONTENT_MAX_WIDTH - 16}px`
    },

    [`& .${classes.buttonContainer}`]: {
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        textAlign: 'center'
    },

    [`& .${classes.addButton}`]: {
        width: '100%',
        marginTop: theme.spacing(1)
    }
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 700,
    fontFamily: `TenantFont, ${theme.typography.fontFamily}`,
    marginBottom: theme.spacing(1),
    color: theme.palette.titleTextColour
}));

interface IRecentOrderItemsSelectionProps {
    order: IOrderReadResourceV18;
    open: boolean;
    onClose: () => void;
    orderingEnabled?: boolean;
}

export const RecentOrderItemsSelection: React.FC<React.PropsWithChildren<IRecentOrderItemsSelectionProps>> =
    ({ open, onClose, order, orderingEnabled }) => {
        const products = React.useMemo(
            () => order.items.filter(item => !item.parentReferenceId),
            [order.items]
        );
        const { t } = useTranslation();
        const { isDesktop } = useResponsive();

        const initialValues = React.useMemo(() => products.map(product => product.id), [products]);
        const dispatch = useDispatch();
        const { getOrderItemsCalories } = useCalories();
        const [selectedProductIds, setSelectedProductIds] = React.useState(initialValues);
        const { merchantId } = useParams<LocationRouteParams>();
        const { logUserEvent } = useGAHelpers();

        const getProductWithModifiers = React.useCallback(
            (product: IItemReadResourceV10 | undefined) => {
                if (!!product) {
                    const modifierProducts = getAllModifierProductsForRootReferenceId(
                        order.items,
                        product.referenceId ?? ''
                    );
                    return [product, ...modifierProducts];
                }
                return [];
            },
            [order.items]
        );

        const handleSubmit = React.useCallback(() => {
            if (isSweetwaters(merchantId)) {
                logUserEvent(EGAEventName.AddFromRecentOrdersSweetwaters);
            }
            const selectedProducts = selectedProductIds.map(value =>
                products.find(product => product.id === value)
            );
            const resultProducts = selectedProducts.reduce<IItemReadResourceV10[]>(
                (acc, product) => [...acc, ...getProductWithModifiers(product)],
                []
            );
            const productsToAdd = resultProducts.reduce<(IBasketItem & { price: number })[]>(
                (acc, product) => {
                    if (!!product) {
                        const {
                            productId,
                            categoryId,
                            quantity,
                            modifiers,
                            referenceId,
                            parentReferenceId,
                            cost
                        } = product;

                        acc.push({
                            productId,
                            categoryId,
                            quantity,
                            modifiers,
                            referenceId: referenceId ?? uuid(),
                            parentReferenceId,
                            price: cost
                        });
                    }

                    return acc;
                },
                []
            );
            addBasketItems(productsToAdd)(dispatch);
            onClose();
        }, [
            dispatch,
            getProductWithModifiers,
            logUserEvent,
            merchantId,
            onClose,
            products,
            selectedProductIds
        ]);

        const handleItemToggle = React.useCallback(
            (id: number) => {
                if (selectedProductIds.includes(id)) {
                    setSelectedProductIds(selectedProductIds.filter(selectedId => selectedId !== id));
                } else {
                    setSelectedProductIds(oldSelectedIds => [...oldSelectedIds, id]);
                }
            },
            [selectedProductIds]
        );

        const renderRecentOrderItemBoxes = React.useCallback(
            (product: IItemReadResourceV10, index: number, array: IItemReadResourceV10[]) => {
                const modifierProducts = getAllModifierProductsForRootReferenceId(
                    order.items,
                    product.referenceId ?? ''
                );
                const nestedModifierProducts = constructBasketItemsTree(modifierProducts);
                const isLast = index === array.length - 1;
                const uniqueKey = `${product.id}-${index}`;
                return isDesktop ? (
                    <Box key={uniqueKey} marginBottom={isLast ? 0 : 1}>
                        <RecentOrderItemBox
                            selected={selectedProductIds.includes(product.id)}
                            item={product}
                            modifierItems={nestedModifierProducts}
                            currencyCode={order.currencyCode}
                            onChange={handleItemToggle}
                            isLast={isLast}
                        />
                    </Box>
                ) : (
                    <RecentOrderItemBox
                        key={uniqueKey}
                        selected={selectedProductIds.includes(product.id)}
                        item={product}
                        modifierItems={nestedModifierProducts}
                        currencyCode={order.currencyCode}
                        onChange={handleItemToggle}
                        isLast={isLast}
                    />
                );
            },
            [handleItemToggle, isDesktop, order.currencyCode, order.items, selectedProductIds]
        );

        const totalPrice = React.useMemo(() => {
            const selectedProducts = order.items.filter(item => selectedProductIds.includes(item.id));
            const allProducts = selectedProducts.reduce<IItemReadResourceV10[]>(
                (acc, item) => [...acc, ...getProductWithModifiers(item)],
                []
            );

            return allProducts.reduce((acc, item) => {
                const [, optionsPrice] = getProductOptionsDetails(item.modifiers);
                return acc + item.quantity * item.cost + optionsPrice * item.quantity;
            }, 0);
        }, [getProductWithModifiers, order.items, selectedProductIds]);

        const showCaloriesMessage = React.useMemo(
            () => getOrderItemsCalories(order.items),
            [getOrderItemsCalories, order.items]
        );

        const paperStyles = React.useMemo(
            () => ({
                paddingTop: 2,
                paddingRight: 2,
                paddingLeft: 2,
                overflowY: 'auto',
                maxHeight: '80vh',
                ...(isDesktop
                    ? {
                          minWidth: 50
                      }
                    : {})
            }),
            [isDesktop]
        );

        return (
            <BottomDialog
                open={open}
                onClose={onClose}
                paperProps={{
                    sx: paperStyles
                }}
            >
                <StyledTitle variant="h5" align="center">
                    {t('PREORDER_RECENT_ORDER_SCREEN_TITLE')}
                </StyledTitle>
                {products.map(renderRecentOrderItemBoxes)}
                <StyledButtonContainer isDesktop={isDesktop}>
                    <div className={classes.buttonWrapper}>
                        <Button
                            color="primary"
                            variant="contained"
                            className={classes.addButton}
                            disabled={!orderingEnabled || !selectedProductIds.length}
                            onClick={handleSubmit}
                        >
                            {t('PREORDER_ADD_TO_BASKET', {
                                quantity: selectedProductIds.length,
                                price: getCurrencyString(totalPrice, order.currencyCode)
                            })}
                        </Button>
                    </div>
                    {showCalories(showCaloriesMessage) && (
                        <Box marginTop={1}>
                            <Typography variant="caption">{t('ORDER_CALORIES_ADVISORY_MESSAGE')}</Typography>
                        </Box>
                    )}
                </StyledButtonContainer>
            </BottomDialog>
        );
    };
